import { createEntityAdapter } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import isEqual from 'lodash.isequal';
import queryString from 'query-string';
import { decamelize } from 'humps';

import deserialize from '../helpers/getroPropertyMapper';
import { parseFiltersData } from './contacts/filters';
import { mapContactsV2ToV1 } from './contactsV2/formatter';
import { baseQuery } from './queries';

const itemsAdapter = createEntityAdapter({
  selectId: (item) => item.id,
});

const itemsSelector = itemsAdapter.getSelectors();

export const sharedCompanyListV2Api = createApi({
  reducerPath: 'sharedCompanyListV2Api',
  baseQuery: (args, api, extraOptions) => baseQuery({ ...args, version: 'v2' }, api, extraOptions),
  tagTypes: ['shared-company-list-contacts'],
  endpoints: (builder) => ({
    getContacts: builder.query({
      query: ({ networkId, filters, sortBy, ...rest }) => {
        const parsedFilters = parseFiltersData(filters, true);

        return {
          url: `/collections/${networkId}/contacts/aggregated`,
          params: {
            ...parsedFilters,
            ...(sortBy ? { sortBy: decamelize(sortBy) } : undefined),
            ...rest,
          },
        };
      },
      transformResponse: (response, _, arg) => ({
        meta: response.meta,
        page: arg.page,
        ...itemsAdapter.addMany(itemsAdapter.getInitialState(), mapContactsV2ToV1(response)),
      }),
      forceRefetch: ({ currentArg, previousArg }) => !isEqual(currentArg, previousArg),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const filters = queryString.stringify(parseFiltersData(queryArgs?.filters || {}, true), {
          arrayFormat: 'bracket',
        });

        return queryArgs?.contextualizedToSharedListId
          ? { contextualizedToSharedListId: queryArgs?.contextualizedToSharedListId?.toString() }
          : `${endpointName}(${filters})`;
      },
      merge: (currentState, incomingState) => {
        if (incomingState.meta.queryTotal === 0) {
          itemsAdapter.removeAll(currentState);
        } else if (currentState.page < incomingState.page) {
          itemsAdapter.setMany(currentState, itemsSelector.selectAll(incomingState));
        } else {
          itemsAdapter.removeAll(currentState);
          itemsAdapter.addMany(currentState, itemsSelector.selectAll(incomingState));
        }

        Object.assign(currentState, {
          meta: incomingState.meta,
        });
      },
      providesTags: ['shared-company-list-contacts'],
    }),
    showMoreContacts: builder.query({
      query: ({ networkId, filters, sortBy, ...rest }) => {
        const parsedFilters = parseFiltersData(filters, true);

        return {
          url: `/collections/${networkId}/contacts/aggregated`,
          params: {
            ...parsedFilters,
            ...(sortBy ? { sortBy: decamelize(sortBy) } : undefined),
            ...rest,
          },
        };
      },
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const company = deserialize(data)[0];

          dispatch(
            sharedCompanyListV2Api.util.updateQueryData('getContacts', args, (draft) => {
              const companyId = args.companyIds[0];
              const result = itemsSelector.selectById(draft, companyId);

              if (result) {
                itemsAdapter.updateOne(draft, {
                  id: companyId,
                  changes: {
                    contacts: company.contacts,
                  },
                });
              }
            }),
          );
        } catch {
          // Do nothing
        }
      },
    }),
  }),
});

export const { useGetContactsQuery, useLazyGetContactsQuery, useShowMoreContactsQuery, useLazyShowMoreContactsQuery } =
  sharedCompanyListV2Api;
